import { Fragment, useMemo } from 'react'
import {
  addDays,
  endOfDay,
  isToday,
  startOfDay,
  startOfWeek,
} from 'date-fns'
import TodayTypo from '../../components/common/TodayTypo'
import { ProcessedEvent } from '../../types'
import { TableGrid } from '../../components/common/TableGrid'
import { MULTI_DAY_EVENT_HEIGHT } from '../../helpers/constants'
import { StackEvents } from '../../components/events/StackEvents'
import { MultiDayEvents } from '../../components/events/MultiDayEvents'
import {
  useSchedulerDispatchContext,
  useSchedulerPropsContext,
} from '../../context/schedulerContext'
import { ISODateString } from '@pogokid/util/datetime'
import { DEFAULT_WEEK_OPTIONS, WeekProps } from './types'
import {
  filterMultiDayEventsOnDays,
  filterSingleDayEventsByDay,
} from '../../helpers/eventFilters.ts'

function useWeekStackedData(
  week: WeekProps | null | undefined,
  selectedDate: ISODateString
) {
  const { weekStartOn, weekDays } = week ?? DEFAULT_WEEK_OPTIONS
  const _weekStart = startOfWeek(new Date(selectedDate), {
    weekStartsOn: weekStartOn,
  })
  const daysList = weekDays.map((d) => addDays(_weekStart, d))
  const weekStart = startOfDay(daysList[0])
  const weekEnd = endOfDay(daysList[daysList.length - 1])

  return {
    daysList,
    weekStart,
    weekEnd,
  }
}

export const WeekStacked = () => {
  const { selectedDate, week, events, calendars, locale } =
    useSchedulerPropsContext()

  const { handleGotoDay } = useSchedulerDispatchContext()

  const { daysList, weekStart, weekEnd } = useWeekStackedData(
    week,
    selectedDate
  )

  const visibleEvents = events.filter((event) =>
    calendars.has(event.calendarId)
  )

  const allMultiDayEventsAcrossTheWeek =
    filterMultiDayEventsOnDays(daysList, visibleEvents)

  // Equalizing multi-day section height
  const headerHeight =
    MULTI_DAY_EVENT_HEIGHT *
      allMultiDayEventsAcrossTheWeek.length +
    45

  return (
    <TableGrid days={daysList.length} indent={''}>
      {/* Header days */}
      {daysList.map((date, i) => (
        <span
          key={i}
          className={`rs__cell rs__header ${
            isToday(date) ? 'rs__today_cell' : ''
          }`}
          style={{ height: headerHeight }}
        >
          <TodayTypo
            date={date}
            onClick={handleGotoDay}
            locale={locale}
          />
          <MultiDayEvents
            events={visibleEvents}
            allWeekMulti={allMultiDayEventsAcrossTheWeek}
            today={date}
            weekStart={weekStart}
            weekEnd={weekEnd}
          />
        </span>
      ))}

      <Fragment>
        {daysList.map((date) => (
          <WeekStackDay
            key={date.valueOf()}
            date={date}
            events={visibleEvents}
          />
        ))}
      </Fragment>
    </TableGrid>
  )
}

interface WeekStackDayProps {
  date: Date
  events: ProcessedEvent[]
}

function WeekStackDay({ date, events }: WeekStackDayProps) {
  const todayEvents = useMemo(
    () => filterSingleDayEventsByDay(date, events),
    [date, events]
  )
  return (
    <span
      className={`rs__cell ${
        isToday(date) ? 'rs__today_cell' : ''
      }`}
    >
      <StackEvents todayEvents={todayEvents} />
    </span>
  )
}
